import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../../components/FeatherIcon'
import { Button, SelectField, ToggleRow } from '../../settings/kit'
import {
  hasWalletOptions,
  INITIAL_RECHARGE,
  INITIAL_RECHARGE_MIN,
  moneyShort,
  OF_ADD_CARD,
  OF_ALTERNATIVE_ADD,
  OF_PAYMENTS,
  RECHARGE_AMOUNTS,
  RECHARGE_MINS,
  topUpPresets,
} from './cardsData'
import { useCards } from './context'

/** The brand name on OnlyFans' PayPal button (its paypal-logo-light sprite). */
const PAYPAL = 'PayPal'
import { EmptyPayments, OfOnly } from './parts'
import { Loader, TransactionsTable } from './Transactions'

/**
 * WalletCredits: the balance, OnlyFans' top-up presets / "Add a payment card",
 * and the wallet's rebill and auto-recharge settings (PATCH users/me).
 */
export function WalletCredits() {
  const { t } = useTranslation('ofMore', { keyPrefix: 'cardsPage' })
  const { me, cards, alternatives, paypal, canWrite, saveSetting } = useCards()
  const [autorecharge, setAutorecharge] = useState(me.isWalletAutorecharge)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (me.isWalletAutorecharge) setAutorecharge(true)
  }, [me.isWalletAutorecharge])

  if (!me.isCreditsEnabled) return null
  const hasCards = (cards?.length ?? 0) > 0
  const showTopUp = (me.canAlternativeWalletTopUp && (alternatives?.length ?? 0) > 0) || hasCards
  const showAddCard = me.canAddCard && !hasCards
  const canPaypalPay = paypal.includes('canPay')
  const canPaypalAdd = paypal.includes('canAdd')
  const options = hasWalletOptions(me)

  /** One saveUserSetting; `busy` is OnlyFans' inProgress for the auto-recharge controls. */
  const save = async (field: Parameters<typeof saveSetting>[0], value: boolean | number, toast = true) => {
    setBusy(true)
    try {
      await saveSetting(field, value, toast)
      return true
    } catch {
      return false
    } finally {
      setBusy(false)
    }
  }

  const onAutorecharge = async (next: boolean) => {
    setAutorecharge(next)
    if (!(await save('isWalletAutorecharge', next))) {
      setAutorecharge(!next)
      return
    }
    if (next) {
      if (!me.walletAutorechargeAmount) await save('walletAutorechargeAmount', INITIAL_RECHARGE, false)
      if (!me.walletAutorechargeMin) await save('walletAutorechargeMin', INITIAL_RECHARGE_MIN, false)
    }
  }

  const mins = RECHARGE_MINS.map((v) => ({ value: String(v), label: t('wallet.minValue', { value: `$${v}` }) }))
  const amounts = RECHARGE_AMOUNTS.map((v) => ({ value: String(v), label: `$${v}` }))
  const blank = { value: '', label: '' }

  return (
    <section className="ofc-box ofc-wallet" aria-label={t('wallet.credits')}>
      <div className="ofc-wallet-head">
        <span className="ofc-balance">{moneyShort(me.creditBalance)}</span>
        {t('wallet.credits')}
      </div>
      <div className={`ofc-wallet-body ${options ? 'has-options' : ''}`}>
        {showTopUp && (
          <div className="ofc-topup">
            <p className="ofc-topup-label">{t('wallet.addFunds')}</p>
            <div className="ofc-presets">
              {topUpPresets(me).map((p) => (
                <button key={p.id} type="button" className="ofc-preset" disabled>
                  ${p.amount}
                </button>
              ))}
            </div>
            <OfOnly text={t('only.topUp')} href={OF_PAYMENTS} />
          </div>
        )}
        {(!showTopUp || showAddCard || canPaypalPay || canPaypalAdd) && (
          <div className="ofc-credits-form">
            {!showTopUp && <div className="ofc-section-title">{t('wallet.addFunds')}</div>}
            {showAddCard && (
              <>
                <Button block disabled>
                  {t('wallet.addPaymentCard')}
                </Button>
                <OfOnly text={t('only.addCard')} href={OF_ADD_CARD} />
              </>
            )}
            {(canPaypalPay || canPaypalAdd) && (
              <>
                <button type="button" className="ofc-paypal" disabled>
                  {canPaypalPay ? t('wallet.paypalTopUp') : t('wallet.paypalAdd')}
                  <strong className="ofc-paypal-logo">{PAYPAL}</strong>
                </button>
                <OfOnly text={canPaypalPay ? t('only.topUp') : t('only.altAdd')} href={canPaypalPay ? OF_PAYMENTS : OF_ALTERNATIVE_ADD} />
              </>
            )}
          </div>
        )}
        {options && (
          <div className={`ofc-wallet-options ${me.isRealCardConnected ? '' : 'is-tight'}`}>
            {me.walletFirstRebills !== undefined && (
              <ToggleRow
                label={t('wallet.firstRebills')}
                checked={me.walletFirstRebills}
                disabled={!canWrite}
                onChange={(next) => void saveSetting('walletFirstRebills', next).catch(() => undefined)}
              />
            )}
            {me.isRealCardConnected && (
              <>
                <ToggleRow
                  label={t('wallet.autorecharge')}
                  help={t('wallet.autorechargeDesc')}
                  checked={autorecharge}
                  disabled={!canWrite || busy}
                  onChange={(next) => void onAutorecharge(next)}
                />
                {autorecharge && (
                  <div className="ofc-recharge">
                    <SelectField
                      label={t('wallet.autorechargeMin')}
                      value={me.walletAutorechargeMin ? String(me.walletAutorechargeMin) : ''}
                      options={me.walletAutorechargeMin ? mins : [blank, ...mins]}
                      disabled={!canWrite || busy}
                      onChange={(v) => v && void save('walletAutorechargeMin', Number(v))}
                    />
                    <SelectField
                      label={t('wallet.autorechargeAmount')}
                      value={me.walletAutorechargeAmount ? String(me.walletAutorechargeAmount) : ''}
                      options={me.walletAutorechargeAmount ? amounts : [blank, ...amounts]}
                      disabled={!canWrite || busy}
                      onChange={(v) => v && void save('walletAutorechargeAmount', Number(v))}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

/** LatestTransactions: the last five, a refresh, and "View more transactions". */
export function LatestTransactions() {
  const { t } = useTranslation('ofMore', { keyPrefix: 'cardsPage' })
  const { hasTransactions, hasTransactionsLoading, latest, latestLoading, reloadLatest, tab, openTab } = useCards()
  const loading = hasTransactionsLoading || latestLoading
  return (
    <section className="ofc-box ofc-latest" aria-label={t('latest.title')}>
      <div className="ofc-latest-head">
        <h2 className={`ofc-section-title ${!loading && !latest.length ? 'is-empty' : ''}`}>{t('latest.title')}</h2>
        {hasTransactions && (
          <button type="button" className={`ofc-icon-btn is-gray ${latestLoading ? 'om-busy' : ''}`} aria-label={t('latest.refresh')} title={t('latest.refresh')} onClick={() => void reloadLatest()}>
            <FeatherIcon name="refresh-cw" size={18} />
          </button>
        )}
      </div>
      {hasTransactionsLoading && <Loader />}
      {!hasTransactionsLoading && hasTransactions && latest.length > 0 && (
        <>
          <TransactionsTable transactions={latest} />
          {tab !== 'list' && (
            <div className="ofc-more-row">
              <Button variant="outline" block onClick={() => openTab('list')}>
                {t('latest.viewMore')}
              </Button>
            </div>
          )}
        </>
      )}
      {!loading && !latest.length && <EmptyPayments text={t('tx.none')} />}
    </section>
  )
}
